import React, { useState, useEffect, useMemo } from 'react';
import { CURRENCY_INFO, convert, subscribeRates } from '../data/exchangeRates';

const HISTORY_KEY = 'etsy-calc-fx-history-v1';
const HISTORY_LIMIT = 10;

const CONVERTER_CCYS = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'JPY', 'CHF',
  'INR', 'MXN', 'BRL', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK',
  'HUF', 'RON', 'BGN', 'HKD', 'SGD', 'MYR', 'IDR', 'PHP',
  'VND', 'ILS', 'TRY', 'ZAR', 'MAD',
];

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};
const saveHistory = (h) => {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h)); }
  catch { /* ignore */ }
};

const fmt = (v) => {
  if (!Number.isFinite(v)) return '';
  if (Math.abs(v) >= 1000) return v.toFixed(2);
  if (Math.abs(v) >= 1) return v.toFixed(4).replace(/\.?0+$/, '');
  return v.toFixed(6).replace(/\.?0+$/, '');
};

// ─── Decorative SVG components ─────────────────────────────────────────
// Hand-drawn globe wireframe — pinned to bottom-right as a decorative watermark
const GlobeArt = () => (
  <svg
    viewBox="0 0 200 200"
    style={{
      position: 'absolute',
      bottom: '-30px',
      right: '-30px',
      width: '220px',
      height: '220px',
      opacity: 0.10,
      pointerEvents: 'none',
    }}
    aria-hidden="true"
  >
    {/* Outer circle (the globe) */}
    <circle cx="100" cy="100" r="90" fill="none" stroke="#c89968" strokeWidth="2.5" />
    {/* Equator + tropics (horizontal ellipses become latitude lines in 3D projection) */}
    <ellipse cx="100" cy="100" rx="90" ry="20" fill="none" stroke="#c89968" strokeWidth="1.5" />
    <ellipse cx="100" cy="100" rx="86" ry="50" fill="none" stroke="#c89968" strokeWidth="1.5" />
    <ellipse cx="100" cy="100" rx="80" ry="80" fill="none" stroke="#c89968" strokeWidth="1.5" strokeDasharray="3 3" />
    {/* Meridians (vertical ellipses) */}
    <ellipse cx="100" cy="100" rx="20" ry="90" fill="none" stroke="#c89968" strokeWidth="1.5" />
    <ellipse cx="100" cy="100" rx="50" ry="86" fill="none" stroke="#c89968" strokeWidth="1.5" />
    {/* North + south pole markers */}
    <circle cx="100" cy="10" r="3" fill="#c89968" />
    <circle cx="100" cy="190" r="3" fill="#c89968" />
    {/* Stylized "land" dots — abstract continents */}
    <circle cx="65" cy="80" r="2" fill="#c89968" opacity="0.7" />
    <circle cx="78" cy="70" r="1.5" fill="#c89968" opacity="0.7" />
    <circle cx="130" cy="85" r="2.5" fill="#c89968" opacity="0.7" />
    <circle cx="145" cy="105" r="2" fill="#c89968" opacity="0.7" />
    <circle cx="55" cy="120" r="2" fill="#c89968" opacity="0.7" />
    <circle cx="115" cy="135" r="1.5" fill="#c89968" opacity="0.7" />
  </svg>
);

// Live rate badge — small pinned thing in the corner showing current FROM→TO rate
const LiveRateBadge = ({ from, to, rate }) => {
  if (!Number.isFinite(rate)) return null;
  const arrow = rate >= 1 ? '↗' : '↘';
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: '14px',
        right: '14px',
        padding: '0.3rem 0.7rem',
        backgroundColor: '#fef3e2',          // soft warm cream
        border: '2px solid #047857',          // emerald-700
        borderRadius: '0.6rem',
        boxShadow: '2px 2px 0px rgba(4,120,87,0.3)',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 600,
        fontSize: '0.88rem',
        letterSpacing: '0.02em',
        color: '#7c2d12',                     // deep amber/bronze
        zIndex: 1,
      }}
    >
      <span style={{ marginRight: '0.25rem' }}>📈</span>
      1 {from} {arrow} {fmt(rate)} {to}
      <span style={{ display: 'block', fontFamily: '"Shadows Into Light", cursive', fontSize: '0.86rem', color: '#b45309', marginTop: '-2px' }}>
        ~ market today ~
      </span>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────
export default function CurrencyConverter() {
  const [fromCcy, setFromCcy] = useState('USD');
  const [toCcy, setToCcy] = useState('EUR');
  const [fromAmount, setFromAmount] = useState('100');
  // setEditing is used in the swap/typing handlers below; the value
  // itself isn't read anywhere, so destructure with a hole.
  // eslint-disable-next-line no-unused-vars
  const [editing, setEditing] = useState('from');
  const [history, setHistory] = useState(loadHistory);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const unsub = subscribeRates(() => forceUpdate(v => v + 1));
    return unsub;
  }, []);

  const toAmount = useMemo(() => {
    const n = parseFloat(fromAmount);
    if (!Number.isFinite(n)) return '';
    return fmt(convert(n, fromCcy, toCcy));
  }, [fromAmount, fromCcy, toCcy]);

  // Live "1 unit" rate for the badge
  const unitRate = convert(1, fromCcy, toCcy);

  const handleToChange = (val) => {
    setEditing('to');
    const n = parseFloat(val);
    if (!Number.isFinite(n)) { setFromAmount(''); return; }
    setFromAmount(fmt(convert(n, toCcy, fromCcy)));
  };
  const handleFromChange = (val) => {
    setEditing('from');
    setFromAmount(val);
  };

  const swap = () => {
    setFromCcy(toCcy);
    setToCcy(fromCcy);
    if (toAmount) setFromAmount(toAmount);
  };

  const save = () => {
    const n = parseFloat(fromAmount);
    if (!Number.isFinite(n) || n <= 0) return;
    const t = convert(n, fromCcy, toCcy);
    if (!Number.isFinite(t)) return;
    const entry = {
      id: Date.now(),
      from: fromCcy, to: toCcy,
      fromAmount: n, toAmount: t,
      savedAt: new Date().toISOString(),
    };
    const filtered = history.filter(
      e => !(e.from === entry.from && e.to === entry.to && e.fromAmount === entry.fromAmount)
    );
    const next = [entry, ...filtered].slice(0, HISTORY_LIMIT);
    setHistory(next);
    saveHistory(next);
  };

  const remove = (id) => {
    const next = history.filter(e => e.id !== id);
    setHistory(next);
    saveHistory(next);
  };

  const clearAll = () => { setHistory([]); saveHistory([]); };
  const ccySymbol = (code) => CURRENCY_INFO[code]?.symbol || code;

  // Card with custom theming
  return (
    <div
      style={{
        position: 'relative',
        background: 'linear-gradient(180deg, #fefdf8 0%, #faf6f0 50%, #f4ecdc 100%)',
        border: '2px solid #1a1a2e',
        borderRadius: '1rem',
        boxShadow: '2px 3px 0 rgba(26,26,46,0.18)',
        padding: '1.5rem 1.25rem 1.25rem',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative art (behind everything) */}
      <GlobeArt />
      <LiveRateBadge from={fromCcy} to={toCcy} rate={unitRate} />

      {/* Foreground content */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        {/* Title — leave room on left for compass + on right for rate badge */}
        <div style={{ paddingRight: '160px', marginBottom: '0.25rem' }}>
          <h2 className="font-display text-2xl text-ink">
            🌐 Currency Converter
          </h2>
          <p className="font-note text-ink/78 mb-4" style={{ fontSize: '1rem' }}>
            Live mid-market rates · convert any currency to any currency.
          </p>
        </div>

        {/* Two-side converter */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-end">
          <div>
            <label className="font-sketch font-bold text-label">From</label>
            <div className="flex items-stretch gap-2 mt-1">
              <input
                type="number"
                value={fromAmount}
                onChange={(e) => handleFromChange(e.target.value)}
                className="sketch-input mt-0"
                style={{ flex: 1, minWidth: 0, backgroundColor: 'rgba(255,255,255,0.85)' }}
                min="0" step="any" placeholder="100"
              />
              <select
                value={fromCcy}
                onChange={(e) => setFromCcy(e.target.value)}
                className="sketch-input mt-0"
                style={{ flex: 'none', width: '5.25rem', paddingRight: '1.5rem', backgroundColor: 'rgba(255,255,255,0.85)' }}
              >
                {CONVERTER_CCYS.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="button"
            onClick={swap}
            title="Swap currencies"
            aria-label="Swap currencies"
            style={{
              alignSelf: 'end',
              marginBottom: '0.1rem',
              width: '2.75rem',
              height: '2.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              cursor: 'pointer',
              border: '2px solid #1a1a2e',
              borderRadius: '0.625rem',
              backgroundColor: '#fefdf8',
              boxShadow: '0 1px 2px rgba(26,26,46,0.04), 0 2px 4px rgba(26,26,46,0.04)',
              transition: 'all 0.1s',
            }}
          >
            ⇄
          </button>

          <div>
            <label className="font-sketch font-bold text-label">To</label>
            <div className="flex items-stretch gap-2 mt-1">
              <input
                type="number"
                value={toAmount}
                onChange={(e) => handleToChange(e.target.value)}
                className="sketch-input mt-0"
                style={{ flex: 1, minWidth: 0, backgroundColor: 'rgba(255,255,255,0.85)' }}
                min="0" step="any" placeholder="—"
              />
              <select
                value={toCcy}
                onChange={(e) => setToCcy(e.target.value)}
                className="sketch-input mt-0"
                style={{ flex: 'none', width: '5.25rem', paddingRight: '1.5rem', backgroundColor: 'rgba(255,255,255,0.85)' }}
              >
                {CONVERTER_CCYS.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Result + Save button */}
        <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
          <p className="font-sketch text-body text-ink/80">
            {fromAmount && toAmount && Number.isFinite(parseFloat(fromAmount)) ? (
              <>
                <span className="font-display text-lg">
                  {ccySymbol(fromCcy)}{fmt(parseFloat(fromAmount))}
                </span>
                {' '}
                <span className="text-ink/40">=</span>
                {' '}
                <span className="font-title text-2xl text-primary">
                  {ccySymbol(toCcy)}{toAmount}
                </span>
              </>
            ) : (
              <span className="text-ink/40">Type an amount above ↑</span>
            )}
          </p>
          <button
            type="button"
            onClick={save}
            disabled={!fromAmount || !toAmount}
            className="sketch-secondary-btn disabled:opacity-40"
          >
            ⭐ save
          </button>
        </div>

        {/* History — entries styled like clipped trading tickets */}
        <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(200, 153, 104, 0.3)' }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-lg text-ink-soft uppercase tracking-wider">📜 Ledger</h3>
            {history.length > 0 && (
              <button
                onClick={clearAll}
                className="font-note text-note text-ink/68 hover:text-alert transition-colors"
              >
                clear all
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <p className="font-note text-note text-ink/68">~ no conversions saved yet ~</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {history.map((e, i) => (
                <li
                  key={e.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0.85rem',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(26, 26, 46, 0.06)',
                    borderRadius: '0.5rem',
                    transform: 'none',
                  }}
                  className="hover:bg-white transition-colors"
                >
                  <span className="font-sketch text-body">
                    <span className="font-numbers text-xl">{ccySymbol(e.from)}{fmt(e.fromAmount)}</span>
                    <span className="text-ink/68"> {e.from} </span>
                    <span className="text-ink/40">→</span>
                    <span className="font-numbers text-xl text-primary"> {ccySymbol(e.to)}{fmt(e.toAmount)}</span>
                    <span className="text-ink/68"> {e.to}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(e.id)}
                    className="text-ink/40 hover:text-alert transition-colors text-xl px-2 leading-none"
                    aria-label="Delete entry"
                    title="Delete"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
